import { useState, useMemo } from "react";
import axios from "axios";
import { Key, Play, CircleNotch, ArrowsClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Live-testable endpoint catalogue ──────────────────────
// Each entry defines editable params with sensible defaults so admins can
// hit "Run" and immediately see a real response.
const API_ENDPOINTS = [
  {
    id: "trees",
    method: "GET",
    path: "/api/v1/trees/estimate",
    desc: "Tree density & count estimation",
    params: [
      { name: "lat", value: "-1.286389", type: "number", step: "0.000001" },
      { name: "lon", value: "36.817223", type: "number", step: "0.000001" },
      { name: "area_km2", value: "1", type: "number", step: "0.1" },
    ],
  },
  {
    id: "carbon",
    method: "GET",
    path: "/api/v1/carbon/estimate",
    desc: "Carbon credit valuation",
    params: [
      { name: "lat", value: "-1.286389", type: "number", step: "0.000001" },
      { name: "lon", value: "36.817223", type: "number", step: "0.000001" },
      { name: "area_km2", value: "1", type: "number", step: "0.1" },
    ],
  },
  {
    id: "climate",
    method: "GET",
    path: "/api/v1/climate",
    desc: "Global CO2, temperature, arctic ice data",
    params: [],
  },
  {
    id: "ndvi",
    method: "GET",
    path: "/api/v1/ndvi",
    desc: "Daily NDVI time series + 7-day predictions",
    params: [
      { name: "lat", value: "-1.286389", type: "number", step: "0.000001" },
      { name: "lon", value: "36.817223", type: "number", step: "0.000001" },
      { name: "start_date", value: new Date(Date.now() - 6 * 86400 * 1000).toISOString().slice(0, 10), type: "date" },
      { name: "end_date", value: new Date().toISOString().slice(0, 10), type: "date" },
    ],
  },
  {
    id: "docs",
    method: "GET",
    path: "/api/v1/docs",
    desc: "Full API documentation with color codes",
    params: [],
  },
];

// A single endpoint tester (isolated state for editing params & showing response).
function EndpointTester({ endpoint, apiKey }) {
  const [params, setParams] = useState(() =>
    endpoint.params.reduce((acc, p) => ({ ...acc, [p.name]: p.value }), {})
  );
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState(null);
  const [durationMs, setDurationMs] = useState(null);
  const [error, setError] = useState("");

  const url = useMemo(() => {
    const qs = new URLSearchParams(params).toString();
    return `${API.replace(/\/api$/, "")}${endpoint.path}${qs ? "?" + qs : ""}`;
  }, [endpoint.path, params]);

  const run = async () => {
    if (!apiKey) {
      setError("Generate an API key above first — the endpoints require an X-API-Key header.");
      return;
    }
    setLoading(true);
    setError("");
    setResponse(null);
    setStatus(null);
    const t0 = performance.now();
    try {
      const r = await axios.get(url, {
        headers: { "X-API-Key": apiKey },
        validateStatus: () => true, // let us render 4xx bodies too
        timeout: 30000,
      });
      setStatus(r.status);
      setResponse(r.data);
      setDurationMs(Math.round(performance.now() - t0));
    } catch (e) {
      setError(e?.message || "Request failed");
      setDurationMs(Math.round(performance.now() - t0));
    } finally {
      setLoading(false);
    }
  };

  const statusColor =
    status == null ? "" :
    status >= 200 && status < 300 ? "bg-emerald-600/20 text-emerald-300 border-emerald-600/40" :
    status >= 400 && status < 500 ? "bg-amber-600/20 text-amber-300 border-amber-600/40" :
    "bg-red-600/20 text-red-300 border-red-600/40";

  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 overflow-hidden"
         data-testid={`api-tester-${endpoint.id}`}>
      <div className="px-4 py-3 border-b border-stone-800 flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-300 tracking-wider">
          {endpoint.method}
        </span>
        <code className="text-emerald-400 text-xs font-mono truncate flex-1 min-w-0">{endpoint.path}</code>
        <span className="text-[11px] text-stone-500 font-body hidden md:inline">— {endpoint.desc}</span>
      </div>

      {endpoint.params.length > 0 && (
        <div className="px-4 pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {endpoint.params.map((p) => (
            <label key={p.name} className="block">
              <span className="text-[10px] text-stone-500 uppercase tracking-wider font-body">{p.name}</span>
              <input
                type={p.type || "text"}
                step={p.step}
                value={params[p.name] ?? ""}
                onChange={(e) => setParams({ ...params, [p.name]: e.target.value })}
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-2 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500 mt-0.5"
                data-testid={`api-tester-${endpoint.id}-${p.name}`}
              />
            </label>
          ))}
        </div>
      )}

      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <button
          onClick={run}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-wait text-white text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
          data-testid={`api-tester-${endpoint.id}-run`}
        >
          {loading ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <Play size={12} weight="fill" />}
          {loading ? "Calling…" : "Run request"}
        </button>
        {status != null && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${statusColor}`}
                data-testid={`api-tester-${endpoint.id}-status`}>
            {status} {status >= 200 && status < 300 ? "OK" : "Error"}
          </span>
        )}
        {durationMs != null && (
          <span className="text-[10px] text-stone-500 font-body">{durationMs} ms</span>
        )}
        <span className="flex-1" />
        {response != null && (
          <button
            onClick={() => { setResponse(null); setStatus(null); setError(""); }}
            className="text-[10px] text-stone-500 hover:text-white inline-flex items-center gap-1"
            data-testid={`api-tester-${endpoint.id}-clear`}
          >
            <ArrowsClockwise size={10} /> Clear
          </button>
        )}
      </div>

      <div className="px-4 pb-3">
        <div className="text-[10px] text-stone-500 uppercase tracking-wider mb-1">Request URL</div>
        <code className="block bg-stone-950 border border-stone-800 rounded p-2 text-[11px] text-stone-300 font-mono break-all">
          {url}
        </code>
      </div>

      {error && (
        <div className="mx-4 mb-3 bg-red-600/10 border border-red-600/30 rounded p-2 text-xs text-red-300"
             data-testid={`api-tester-${endpoint.id}-error`}>
          {error}
        </div>
      )}

      {response != null && (
        <div className="px-4 pb-4">
          <div className="text-[10px] text-stone-500 uppercase tracking-wider mb-1">Response</div>
          <pre className="bg-stone-950 border border-stone-800 rounded p-3 text-[11px] text-emerald-200 font-mono overflow-auto max-h-80"
               data-testid={`api-tester-${endpoint.id}-response`}>
            {JSON.stringify(response, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function ApiKeysTab({ apiKeys, keyName, setKeyName, newKey, onGenKey, onRevokeKey }) {
  // The tester uses the most recently generated key when the admin has just
  // created one. Existing keys from the DB are masked (showed as "gs_abcd…1234")
  // and cannot be replayed — that's why the tester requires a fresh key.
  const [manualKey, setManualKey] = useState("");
  const activeKey = manualKey.trim() || newKey || "";
  const hasUsableKey = Boolean(activeKey);

  return (
    <div className="space-y-6" data-testid="admin-api-keys-tab">
      <div>
        <h2 className="font-heading text-xl font-semibold mb-1">Developer API Keys</h2>
        <p className="text-xs text-stone-500 font-body">
          Generate API keys to allow external developers and B2B partners to access Conexus Foundation data programmatically.
          Keys provide access to tree density, carbon credit, NDVI, and climate data endpoints.
        </p>
      </div>

      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5">
        <h3 className="text-sm font-heading font-medium mb-3">Generate New API Key</h3>
        <div className="flex gap-3">
          <input value={keyName} onChange={(e) => setKeyName(e.target.value)}
            placeholder="Key name (e.g., Production, Testing)"
            className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            data-testid="api-key-name-input" />
          <button onClick={onGenKey}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-lg flex items-center gap-1.5"
            data-testid="generate-key-btn">
            <Key size={14} weight="bold" /> Generate
          </button>
        </div>
        {newKey && (
          <div className="mt-3 bg-emerald-600/10 border border-emerald-600/30 rounded-lg p-3" data-testid="new-api-key-display">
            <p className="text-xs text-emerald-400 mb-1">Your new API key (copy it now — it won&apos;t be shown again):</p>
            <code className="text-sm text-white font-mono break-all">{newKey}</code>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {apiKeys.map((k) => (
          <div key={k.id} className="bg-stone-900 rounded-xl border border-stone-800 px-4 py-3 flex items-center gap-4"
               data-testid={`api-key-${k.id}`}>
            <Key size={16} className="text-violet-400" />
            <div className="flex-1">
              <p className="text-sm text-white">{k.name}</p>
              <p className="text-xs text-stone-500 font-mono">{k.key}</p>
            </div>
            <span className="text-xs text-stone-500">{k.requests_count || 0} requests</span>
            <button onClick={() => onRevokeKey(k.id)}
              className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-600/10"
              data-testid={`revoke-key-${k.id}`}>
              Revoke
            </button>
          </div>
        ))}
      </div>

      {/* ─── Live API tester ───────────────────────────────── */}
      <div className="bg-stone-900/50 rounded-xl border border-stone-800/50 p-5 space-y-4">
        <div>
          <h3 className="text-sm font-heading font-medium mb-1">Live API Playground</h3>
          <p className="text-xs text-stone-500 font-body">
            Edit the parameters, click <strong>Run request</strong>, and see the real JSON response the API returns. Every call uses
            the <code className="bg-stone-800 px-1 py-0.5 rounded">X-API-Key</code> header — increment your key&apos;s request counter on each hit.
          </p>
        </div>

        <div className="bg-stone-900 rounded-lg border border-stone-800 p-3">
          <label className="block">
            <span className="text-[10px] text-stone-500 uppercase tracking-wider">API key for testing</span>
            <input
              type="text"
              value={manualKey}
              onChange={(e) => setManualKey(e.target.value)}
              placeholder={newKey ? `Using freshly generated key (${newKey.slice(0, 10)}…) — or paste another here` : "Paste a full API key (gs_…) to test"}
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-emerald-500 mt-1"
              data-testid="api-tester-key-input"
            />
          </label>
          {!hasUsableKey && (
            <p className="text-[11px] text-amber-300 mt-2">
              Generate a new key above (or paste an existing full-length one) — the stored keys shown below are masked and can&apos;t be replayed.
            </p>
          )}
          {hasUsableKey && (
            <p className="text-[11px] text-emerald-300 mt-2" data-testid="api-tester-key-ready">
              Using key <code className="bg-stone-800 px-1 py-0.5 rounded">{activeKey.slice(0, 10)}…{activeKey.slice(-4)}</code> for all tests below.
            </p>
          )}
        </div>

        <div className="space-y-3">
          {API_ENDPOINTS.map((e) => (
            <EndpointTester key={e.id} endpoint={e} apiKey={activeKey} />
          ))}
        </div>
      </div>
    </div>
  );
}
